import { useCallback, useEffect, useRef, useState } from "react";
import { getRouteApi } from "@tanstack/react-router";
import { LoaderCircle, Search, X } from "lucide-react";

import type { DirectoryCounts, DirectoryLink } from "@/api/db/people";
import { ALL_SOURCES, SOURCE_LABEL } from "@/components/people/source-badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { useDebouncedSearch } from "@/hooks/use-debounced-search";
import { FunnelLabels } from "@/lib/labels";
import { PessoasLabels } from "@/lib/labels/pessoas";
import { cn } from "@/lib/utils";

const routeApi = getRouteApi("/_app/pessoas/");
const LINKS: DirectoryLink[] = ["patient_confirmed", "patient_potential", "only_conversation"];

export function PessoasControls({
	counts,
	total,
	isFetching,
}: {
	counts: DirectoryCounts | undefined;
	total: number | undefined;
	isFetching: boolean;
}) {
	const search = routeApi.useSearch();
	const navigate = routeApi.useNavigate();
	const [term, setTerm] = useState(search.q ?? "");
	const committed = useRef(search.q ?? "");

	useEffect(() => {
		if (committed.current !== (search.q ?? "")) {
			committed.current = search.q ?? "";
			setTerm(search.q ?? "");
		}
	}, [search.q]);

	const commitSearch = useCallback(
		(value: string) => {
			if (value === (search.q ?? "")) {
				return;
			}

			committed.current = value;
			navigate({ search: (prev) => ({ ...prev, q: value || undefined, page: 1 }) });
		},
		[navigate, search.q],
	);

	useDebouncedSearch(term, commitSearch);

	function handleClearFilters() {
		committed.current = "";
		setTerm("");
		navigate({ search: { page: 1 } });
	}

	const stageEntries = Object.entries(counts?.stage ?? {})
		.filter(([value]) => value !== "none")
		.sort((a, b) => b[1] - a[1]);
	const hasFilters =
		!!term ||
		!!search.link ||
		!!search.source ||
		!!search.stage ||
		search.problems ||
		search.include_non_human;
	const isSearching = isFetching || term.trim() !== (search.q ?? "");

	return (
		<div className="flex flex-col gap-4 p-4 sm:p-5">
			<div className="relative">
				<Search
					aria-hidden="true"
					className="pointer-events-none absolute left-4 top-1/2 size-5 -translate-y-1/2 text-muted-foreground"
				/>
				<Input
					type="search"
					data-slot="search"
					aria-label="Buscar pessoas por nome, CPF, telefone ou email"
					value={term}
					onChange={(event) => setTerm(event.target.value)}
					placeholder="Nome, CPF, telefone ou email"
					className="h-12 rounded-lg bg-card pl-12 pr-4 shadow-control md:h-12 md:text-base"
				/>
			</div>

			<div className="flex flex-wrap items-center gap-3">
				<div
					data-component="pessoas-link-facet"
					role="group"
					aria-label="Vínculo com a clínica"
					className="grid w-full grid-cols-2 gap-1 rounded-lg bg-secondary p-1 sm:flex sm:w-auto sm:flex-wrap sm:rounded-full"
				>
					<PessoasLinkFilter
						slot="link-all"
						label={PessoasLabels.all}
						count={counts && Object.values(counts.link).reduce((sum, count) => sum + count, 0)}
						active={search.link === undefined}
						onClick={() => navigate({ search: (prev) => ({ ...prev, link: undefined, page: 1 }) })}
					/>
					{LINKS.map((value) => (
						<PessoasLinkFilter
							key={value}
							slot={`link-${value}`}
							label={PessoasLabels.linkShort[value]}
							count={counts?.link[value]}
							active={search.link === value}
							onClick={() => navigate({ search: (prev) => ({ ...prev, link: value, page: 1 }) })}
						/>
					))}
				</div>

				<Select
					data-component="pessoas-source-facet"
					aria-label="Filtrar por fonte"
					value={search.source ?? ""}
					onChange={(event) =>
						navigate({
							search: (prev) => ({ ...prev, source: event.target.value || undefined, page: 1 }),
						})
					}
					className={cn(
						"w-auto max-w-full bg-card",
						search.source && "border-primary/30 bg-primary-soft text-primary",
					)}
				>
					<option value="">Todas as fontes</option>
					{ALL_SOURCES.map((value) => (
						<option key={value} value={value}>
							{SOURCE_LABEL[value]}
						</option>
					))}
				</Select>

				<Select
					data-component="pessoas-stage-facet"
					aria-label={PessoasLabels.stage}
					value={search.stage ?? ""}
					onChange={(event) =>
						navigate({
							search: (prev) => ({ ...prev, stage: event.target.value || undefined, page: 1 }),
						})
					}
					className={cn(
						"w-auto max-w-full bg-card",
						search.stage && "border-primary/30 bg-primary-soft text-primary",
					)}
				>
					<option value="">Todas as etapas</option>
					<option value="none">{PessoasLabels.noStage}</option>
					{search.stage &&
						search.stage !== "none" &&
						!stageEntries.some(([value]) => value === search.stage) && (
							<option value={search.stage}>{FunnelLabels.stage(search.stage)}</option>
						)}
					{stageEntries.map(([value]) => (
						<option key={value} value={value}>
							{FunnelLabels.stage(value)}
						</option>
					))}
				</Select>

				<button
					type="button"
					data-slot="problems-filter"
					aria-pressed={search.problems}
					onClick={() =>
						navigate({ search: (prev) => ({ ...prev, problems: !prev.problems, page: 1 }) })
					}
					className={cn(
						"inline-flex min-h-11 items-center gap-2 rounded-md border px-3 text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:min-h-9",
						search.problems
							? "border-destructive/30 bg-destructive-soft text-destructive"
							: "border-border bg-card text-muted-foreground hover:bg-secondary hover:text-foreground",
					)}
				>
					<span aria-hidden="true" className="size-1.5 rounded-full bg-destructive" />
					{PessoasLabels.problemFilter}
					{counts !== undefined && counts.problems > 0 && (
						<span className="font-mono text-xs">{counts.problems.toLocaleString("pt-BR")}</span>
					)}
				</button>
			</div>

			<div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-border pb-4 text-xs text-muted-foreground">
				<label
					data-slot="include-non-human"
					className="inline-flex min-h-9 cursor-pointer items-center gap-2"
				>
					<input
						type="checkbox"
						checked={search.include_non_human}
						onChange={() =>
							navigate({
								search: (prev) => ({
									...prev,
									include_non_human: !prev.include_non_human,
									page: 1,
								}),
							})
						}
						className="size-4 rounded border-border accent-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
					/>
					Incluir não-humanas
				</label>
				{hasFilters && (
					<Button type="button" variant="ghost" size="sm" onClick={handleClearFilters}>
						<X aria-hidden="true" />
						Limpar filtros
					</Button>
				)}
				<span
					role="status"
					aria-live="polite"
					className="ml-auto inline-flex min-h-9 items-center gap-2 tabular-nums"
				>
					{isSearching && (
						<>
							<LoaderCircle aria-hidden="true" className="size-3.5 animate-spin" />
							Buscando pessoas...
						</>
					)}
					{!isSearching && total !== undefined && (
						<>
							<b className="font-semibold text-foreground/80">{total.toLocaleString("pt-BR")}</b>
							{total === 1 && "resultado"}
							{total !== 1 && PessoasLabels.results}
						</>
					)}
				</span>
			</div>
		</div>
	);
}

function PessoasLinkFilter({
	slot,
	label,
	count,
	active,
	onClick,
}: {
	slot: string;
	label: string;
	count: number | undefined;
	active: boolean;
	onClick: () => void;
}) {
	return (
		<button
			type="button"
			data-slot={slot}
			aria-pressed={active}
			onClick={onClick}
			className={cn(
				"inline-flex min-h-10 items-center justify-between gap-2 rounded-md px-3 text-xs transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring sm:justify-center sm:rounded-full md:min-h-8",
				active
					? "bg-card font-medium text-foreground shadow-control"
					: "text-muted-foreground hover:text-foreground",
			)}
		>
			{label}
			{count !== undefined && (
				<span className={cn("font-mono text-xs tabular-nums", active && "text-primary")}>
					{count.toLocaleString("pt-BR")}
				</span>
			)}
		</button>
	);
}
